/*
  <solvera-delivery-estimator> — PDP delivery estimator.

  TRUTH RULE, and it shapes the whole design: an estimator that invents "arrives
  Tuesday" with no logistics data behind it is a fabricated claim, which this brand
  treats as a P0 defect exactly like a fake stock counter. So every row is rendered
  only from something the merchant has actually declared:

    • the delivery WINDOW appears only when dispatch and transit days are configured,
      and is always labelled ESTIMATED with an explicit "not guaranteed" caveat
    • "Ships in", free shipping, returns and exchange are free-text facts the merchant
      owns; a blank setting hides its row entirely rather than inventing a default
    • COD appears only when COD pincode prefixes are declared
    • serviceability is claimed only from a configured prefix list or an explicit
      nationwide flag

  A real carrier API attaches without touching this file or the markup:

      window.SolveraDelivery.setProvider(async (pincode, config) => ({
        serviceable: true, etaFrom: Date, etaTo: Date, cod: true, source: 'carrier'
      }));

  A provider returning source:'carrier' switches the caveat from "estimated" to the
  configured wording, so a real API is visibly distinguished from a projection.

  Every customer-facing string arrives via data-* from Liquid, so nothing is
  hardcoded here and all copy stays in locales/.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'solvera:pincode';
  var PIN_RE = /^[1-9][0-9]{5}$/; // Indian PIN: six digits, never a leading zero.

  /* Inline SVG. Kept inline deliberately: no extra request, no flash of missing
     icon, and no dependency on a sprite that a future theme update might drop.
     All are 16x16, currentColor, stroke-based to match the type weight. */
  var ICON = {
    pin: '<path d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 3.4 4.5 8.5 4.5 8.5s4.5-5.1 4.5-8.5A4.5 4.5 0 0 0 8 1.5Z"/><circle cx="8" cy="6" r="1.6"/>',
    truck: '<path d="M1.5 4.5h8v6h-8zM9.5 7h2.6l2.4 2.2v1.3h-5z"/><circle cx="4.5" cy="12" r="1.3"/><circle cx="11.5" cy="12" r="1.3"/>',
    clock: '<circle cx="8" cy="8" r="6.2"/><path d="M8 4.6V8l2.4 1.6"/>',
    rupee: '<path d="M5 3h6M5 6h6M10 3c0 2.2-1.6 3.4-3.6 3.4H5l4.6 6.1"/>',
    wallet: '<path d="M2.2 4.4h11.6v8.2H2.2zM2.2 4.4 10.6 2v2.4"/><circle cx="11.2" cy="8.5" r="0.9"/>',
    refresh: '<path d="M13.4 7a5.5 5.5 0 0 0-9.6-2.6M2.6 9a5.5 5.5 0 0 0 9.6 2.6"/><path d="M13.4 3.4V7H9.9M2.6 12.6V9h3.5"/>',
    swap: '<path d="M3 5.6h8.4L9 3.2M13 10.4H4.6L7 12.8"/>',
    check: '<path d="M3.2 8.4 6.4 11.6 12.8 5.2"/>',
  };

  function icon(name) {
    return (
      '<svg class="sv-de__icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" ' +
      'stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      (ICON[name] || '') +
      '</svg>'
    );
  }

  /* Default provider. Deliberately conservative: it returns only what the merchant
     configured and null for everything it cannot know. */
  function defaultProvider(pincode, config) {
    var serviceable = null;
    if (config.servicePrefixes.length) {
      serviceable = config.servicePrefixes.some(function (p) {
        return pincode.indexOf(p) === 0;
      });
    } else if (config.shipsNationwide) {
      serviceable = true;
    }

    var cod = null;
    if (config.codPrefixes.length) {
      cod = config.codPrefixes.some(function (p) {
        return pincode.indexOf(p) === 0;
      });
    }

    // A window is produced ONLY from configured numbers. There is no fallback guess.
    var etaFrom = null;
    var etaTo = null;
    if (config.dispatchDays !== null && config.transitMin !== null && config.transitMax !== null) {
      var base = new Date();
      base.setHours(0, 0, 0, 0);
      etaFrom = addBusinessDays(base, config.dispatchDays + config.transitMin);
      etaTo = addBusinessDays(base, config.dispatchDays + config.transitMax);
    }

    return { serviceable: serviceable, cod: cod, etaFrom: etaFrom, etaTo: etaTo, source: 'configured' };
  }

  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) added += 1; // skip weekends
    }
    return d;
  }

  function formatDate(d, locale) {
    try {
      return d.toLocaleDateString(locale || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (e) {
      return d.toDateString();
    }
  }

  var provider = defaultProvider;

  window.SolveraDelivery = window.SolveraDelivery || {};
  window.SolveraDelivery.setProvider = function (fn) {
    if (typeof fn === 'function') provider = fn;
  };
  window.SolveraDelivery.getPincode = function () {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  };

  var Estimator = function () {
    return Reflect.construct(HTMLElement, [], Estimator);
  };
  Estimator.prototype = Object.create(HTMLElement.prototype);
  Estimator.prototype.constructor = Estimator;
  Object.setPrototypeOf(Estimator, HTMLElement);

  Estimator.prototype.connectedCallback = function () {
    var el = this;
    this.form = this.querySelector('[data-sv-de-form]');
    this.input = this.querySelector('[data-sv-de-input]');
    this.button = this.querySelector('[data-sv-de-submit]');
    this.result = this.querySelector('[data-sv-de-result]');
    if (!this.form || !this.input || !this.result) return;

    var ds = this.dataset;
    this.config = {
      dispatchDays: numOrNull(ds.dispatchDays),
      transitMin: numOrNull(ds.transitMin),
      transitMax: numOrNull(ds.transitMax),
      servicePrefixes: listOf(ds.servicePrefixes),
      codPrefixes: listOf(ds.codPrefixes),
      shipsNationwide: ds.shipsNationwide === 'true',
      shipsIn: (ds.shipsIn || '').trim(),
      freeShipping: (ds.freeShipping || '').trim(),
      returns: (ds.returns || '').trim(),
      exchange: (ds.exchange || '').trim(),
      codNote: (ds.codNote || '').trim(),
      codFee: (ds.codFee || '').trim(),
    };
    this.s = {
      invalid: ds.stringInvalid || '',
      serviceable: ds.stringServiceable || '',
      notServiceable: ds.stringNotServiceable || '',
      eta: ds.stringEta || '',
      etaLabel: ds.stringEtaLabel || '',
      codLabel: ds.stringCodLabel || '',
      codYes: ds.stringCodYes || '',
      codNo: ds.stringCodNo || '',
      deliveringTo: ds.stringDeliveringTo || '',
      change: ds.stringChange || '',
      shipsInLabel: ds.stringShipsInLabel || '',
      freeShippingLabel: ds.stringFreeShippingLabel || '',
      returnsLabel: ds.stringReturnsLabel || '',
      exchangeLabel: ds.stringExchangeLabel || '',
      codNoteLabel: ds.stringCodNoteLabel || '',
      estimatedCaveat: ds.stringEstimatedCaveat || '',
      configuredCaveat: ds.stringConfiguredCaveat || '',
      checkoutNote: ds.stringCheckoutNote || '',
      error: ds.stringError || '',
    };

    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      el.check(el.input.value.trim());
    });

    // Delegated so it survives every re-render of the result region.
    this.result.addEventListener('click', function (e) {
      var t = e.target.closest('[data-sv-de-change]');
      if (!t) return;
      e.preventDefault();
      el.form.hidden = false;
      el.result.innerHTML = '';
      el.input.focus();
    });

    var saved = window.SolveraDelivery.getPincode();
    if (saved && PIN_RE.test(saved)) {
      this.input.value = saved;
      this.check(saved, true);
    }
  };

  Estimator.prototype.check = function (pincode, silent) {
    var el = this;

    if (!PIN_RE.test(pincode)) {
      this.input.setAttribute('aria-invalid', 'true');
      this.render('<p class="sv-de__error">' + esc(this.s.invalid) + '</p>');
      return;
    }
    this.input.removeAttribute('aria-invalid');

    try {
      window.localStorage.setItem(STORAGE_KEY, pincode);
    } catch (e) {
      /* private mode — persistence is a convenience, never a requirement */
    }

    if (!silent) {
      this.button.setAttribute('aria-busy', 'true');
      this.render(skeleton());
    }

    Promise.resolve()
      .then(function () {
        return provider(pincode, el.config);
      })
      .then(function (res) {
        el.button.removeAttribute('aria-busy');
        el.render(el.markup(res || {}, pincode));
        el.form.hidden = true; // collapse the input once answered; "Change" restores it
      })
      .catch(function () {
        el.button.removeAttribute('aria-busy');
        el.render('<p class="sv-de__error">' + esc(el.s.error) + '</p>');
      });
  };

  Estimator.prototype.markup = function (res, pincode) {
    var s = this.s;
    var c = this.config;
    var locale = document.documentElement.lang || undefined;

    if (res.serviceable === false) {
      return (
        '<p class="sv-de__error">' + esc(s.notServiceable) + '</p>' +
        '<button type="button" class="sv-de__change" data-sv-de-change>' + esc(s.change) + '</button>'
      );
    }

    var head =
      '<div class="sv-de__head">' +
      '<span class="sv-de__head-check">' + icon('check') + '</span>' +
      '<span class="sv-de__head-text">' + esc(s.deliveringTo.replace('{{ pincode }}', pincode)) + '</span>' +
      '<button type="button" class="sv-de__change" data-sv-de-change>' + esc(s.change) + '</button>' +
      '</div>';

    var rows = [];

    // Estimated delivery — only from configured numbers, always caveated.
    if (res.etaFrom && res.etaTo) {
      rows.push(
        row('truck', s.etaLabel, s.eta.replace('{{ from }}', formatDate(res.etaFrom, locale)).replace('{{ to }}', formatDate(res.etaTo, locale)), true)
      );
    }
    if (c.shipsIn) rows.push(row('clock', s.shipsInLabel, c.shipsIn));
    if (c.freeShipping) rows.push(row('rupee', s.freeShippingLabel, c.freeShipping));
    // Per-pincode COD wins when the merchant has declared prefixes; otherwise the
    // general COD statement is used. Never both, and never a guess.
    if (res.cod === true) rows.push(row('wallet', s.codLabel, s.codYes));
    else if (res.cod === false) rows.push(row('wallet', s.codLabel, s.codNo));
    else if (c.codNote) {
      // Append the fee when one exists: the FAQ schema already discloses it to
      // Google, so hiding it from the customer would be the dishonest half.
      var codText = c.codFee ? c.codNote + ' — ' + c.codFee : c.codNote;
      rows.push(row('wallet', s.codNoteLabel, codText));
    }
    if (c.returns) rows.push(row('refresh', s.returnsLabel, c.returns));
    if (c.exchange) rows.push(row('swap', s.exchangeLabel, c.exchange));

    // Caveat: a real carrier response is labelled differently from a projection.
    var caveat = '';
    if (res.etaFrom && res.etaTo) {
      caveat = res.source === 'carrier' ? s.configuredCaveat : s.estimatedCaveat;
    } else {
      caveat = s.checkoutNote;
    }

    return (
      head +
      (rows.length ? '<dl class="sv-de__rows">' + rows.join('') + '</dl>' : '') +
      '<p class="sv-de__note">' + esc(caveat) + '</p>'
    );
  };

  Estimator.prototype.render = function (html) {
    this.result.innerHTML = html;
  };

  function row(name, label, value, estimated) {
    return (
      '<div class="sv-de__row' + (estimated ? ' sv-de__row--primary' : '') + '">' +
      '<dt class="sv-de__row-label">' + icon(name) + '<span>' + esc(label) + '</span></dt>' +
      '<dd class="sv-de__row-value">' + esc(value) + '</dd>' +
      '</div>'
    );
  }

  // Fixed-height skeleton so replacing it with real rows causes no layout shift.
  function skeleton() {
    return (
      '<div class="sv-de__skeleton" aria-hidden="true">' +
      '<span class="sv-de__skeleton-bar sv-de__skeleton-bar--head"></span>' +
      '<span class="sv-de__skeleton-bar"></span>' +
      '<span class="sv-de__skeleton-bar"></span>' +
      '</div>'
    );
  }

  function numOrNull(v) {
    if (v === undefined || v === null || String(v).trim() === '') return null;
    var n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  }

  function listOf(v) {
    if (!v) return [];
    return v.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  if (!customElements.get('solvera-delivery-estimator')) {
    customElements.define('solvera-delivery-estimator', Estimator);
  }
})();
